import { Category } from "@mui/icons-material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CarIcon from "@mui/icons-material/DirectionsCar";
import DiscountOutlinedIcon from "@mui/icons-material/DiscountOutlined";
import DocumentScannerOutlined from "@mui/icons-material/DocumentScannerOutlined";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import GppBadOutlinedIcon from "@mui/icons-material/GppBadOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HomeIcon from "@mui/icons-material/Home";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import MoneyIcon from "@mui/icons-material/Money";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { api } from "common";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import RolesAPI from "../common/rolesApi";
import { colors } from "../components/Theme/WebTheme";

const drawerWidth = 260;

export default function ResponsiveDrawer(props) {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const { window } = props;
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const { signOff } = api;
    const auth = useSelector((state) => state.auth);
    const settings = useSelector((state) => state.settingsdata.settings);
    const dispatch = useDispatch();
    const addonsdata = useSelector((state) => state.addondata);

    // Debug addon loading
    useEffect(() => {
        console.log("🔍 ResponsiveDrawer - Addon State:", {
            full_state: addonsdata,
            data: addonsdata?.data,
            loading: addonsdata?.loading,
            error: addonsdata?.error,
            sub_admin_controls: addonsdata?.data?.sub_admin_controls,
        });
    }, [addonsdata]);

    const LogOut = () => {
        dispatch(signOff());
    };

    const [role, setRole] = useState(null);
    const [userPermissions, setUserPermissions] = useState([]);

    const isSubMenuAdminPermissionGranted = (permissionName) => {
        try {
            if (
                addonsdata.data &&
                addonsdata.data.sub_admin_controls === true
            ) {
                if (!auth.profile.subAdminPermissions) {
                    return true;
                }
                if (
                    auth.profile.subAdminPermissions.type === "all" ||
                    auth.profile.subAdminPermissions.type === ""
                ) {
                    return true;
                } else if (
                    auth.profile.subAdminPermissions.type === "specific"
                ) {
                    console.log(
                        "🔍 Specific permissions for:",
                        auth.profile.subAdminPermissions.pages[permissionName],
                        permissionName
                    );
                    return auth.profile.subAdminPermissions.pages[
                        permissionName
                    ];
                } else {
                    return false;
                }
            }
            return true;
        } catch (error) {
            return true;
        }
    };

    const fetchUserPermissions = useCallback(async () => {
        try {
            const response = await RolesAPI.getUserRole(auth.profile.uid);

            if (response.success && response.user) {
                console.log(
                    "🔍 User permissions fetched:",
                    response.user.permissions
                );
                setUserPermissions(response.user.permissions || []);
            } else {
                console.log("🔍 No custom permissions found, using default");
                setUserPermissions([]);
            }
        } catch (error) {
            console.error("Error fetching user permissions:", error);
            setUserPermissions([]);
        }
    }, [auth.profile.uid]);
    useEffect(() => {
        if (auth.profile && auth.profile.usertype) {
            setRole(auth.profile.usertype);

            // Fetch user permissions for custom roles
            if (auth.profile.uid) {
                fetchUserPermissions();
            }
        }
    }, [auth.profile, fetchUserPermissions]);

    useEffect(() => {
        if (auth.profile && auth.profile.usertype) {
            setRole(auth.profile.usertype);

            // Fetch user permissions for custom roles
            if (auth.profile.uid) {
                fetchUserPermissions();
            }
        }
    }, [auth.profile, fetchUserPermissions]);

    // Helper function to check if user has access to a menu item
    const hasMenuAccess = (menuItem) => {
        console.log(
            "🔍 Checking access for menu:",
            menuItem.name,
            "permission:",
            menuItem.permission
        );

        // Special case: essential menu items should always be available for all users
        const essentialMenus = ["logout", "profile", "home"];
        if (essentialMenus.includes(menuItem.permission)) {
            console.log("🔍 Essential menu always available:", menuItem.name);
            return true;
        }

        // Special case: subscription menu should be hidden if subscriptions are disabled
        if (
            menuItem.permission === "subscriptions" &&
            settings &&
            settings.enable_subscriptions === false
        ) {
            console.log(
                "🔍 Subscription menu hidden - subscriptions disabled in settings"
            );
            return false;
        }

        // Special case: withdraws menu should be hidden if manual_withdraw_add_on is disabled
        if (
            menuItem.permission === "withdraws" &&
            settings &&
            settings.manual_withdraw_add_on !== true
        ) {
            console.log(
                "🔍 Withdraws menu hidden - manual_withdraw_add_on not enabled in settings"
            );
            return false;
        }

        // Special case: document management should be available for admin
        if (menuItem.permission === "document_management" && role === "admin") {
            console.log(
                "🔍 Document management available for admin:",
                menuItem.name
            );
            return true;
        }

        // special case for admin not showing my wallet
        if (menuItem.permission === "wallet" && role === "admin") {
            console.log("🔍 My wallet not available for admin:", menuItem.name);
            return false;
        }

        if (
            menuItem.permission === "complain" &&
            settings.support_module_enabled === true
        ) {
            console.log("🔍 Complain available for admin:", menuItem.name);
            return false;
        }

        if (
            menuItem.permission === "support_management" &&
            role === "admin" &&
            settings.support_module_enabled === true
        ) {
            console.log(
                "🔍 Support management available for admin:",
                menuItem.name
            );
            return true;
        }

        // Special case: bank details should be available for admin
        if (menuItem.permission === "bank_account" && role === "admin") {
            console.log("🔍 Bank details available for admin:", menuItem.name);
            return true;
        }

        // Debug for Bank Account specifically
        if (menuItem.permission === "bank_account") {
            console.log("🔍 Bank Account menu check:", {
                name: menuItem.name,
                permission: menuItem.permission,
                role: role,
                userPermissions: userPermissions,
                hasAccess: menuItem.access.includes(role),
            });
        }

        // Prioritize custom role over usertype (even for admin)
        if (userPermissions.length > 0) {
            console.log(
                "🔍 Using custom role permissions for menu:",
                menuItem.name,
                userPermissions
            );
            return userPermissions.includes(menuItem.permission);
        }

        // Only if no custom role, then check admin usertype
        if (role === "admin") {
            console.log(
                "🔍 Admin user without custom role, allowing menu access:",
                menuItem.name
            );
            return true;
        }

        // Fallback to default access control for other usertypes
        console.log(
            "🔍 Using default access control for menu:",
            menuItem.name,
            role
        );
        return menuItem?.access?.includes(role);
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const drawer = (
        <div style={{ backgroundColor: colors.MAIN_COLOR, height: "100%" }}>
            <div
                style={{
                    display: "flex",
                    backgroundColor: colors.MAIN_COLOR,
                    justifyContent: "center",
                }}
            >
                <img
                    style={{
                        marginTop: "20px",
                        marginBottom: "20px",
                        width: "120px",
                    }}
                    src={require("../assets/img/logo192x192.png").default}
                    alt="Logo"
                />
            </div>
            <div style={{ backgroundColor: colors.MAIN_COLOR }}>
                {role ? (
                    <List disablePadding={true} dense={false}>
                        {[
                            {
                                name: t("home"),
                                url: "/",
                                icon: <HomeIcon />,
                                access: [
                                    "admin",
                                    "fleetadmin",
                                    "driver",
                                    "customer",
                                ],
                                permission: "home",
                            },
                            ...(isSubMenuAdminPermissionGranted("dashboard")
                                ? [
                                      {
                                          name: t("dashboard_text"),
                                          url: "/dashboard",
                                          icon: <DashboardIcon />,
                                          access: ["admin", "fleetadmin"],
                                          permission: "dashboard",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("bookings")
                                ? [
                                      {
                                          name: t("booking_history"),
                                          url: "/bookings",
                                          icon: <FormatListBulletedIcon />,
                                          access: [
                                              "admin",
                                              "fleetadmin",
                                              "driver",
                                              "customer",
                                          ],
                                          permission: "booking_history",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("addbookings")
                                ? [
                                      {
                                          name: t("addbookinglable"),
                                          url: "/addbookings",
                                          icon: <AddLocationAltOutlinedIcon />,
                                          access: [
                                              "admin",
                                              "fleetadmin",
                                              "customer",
                                          ],
                                          permission: "add_bookings",
                                      },
                                  ]
                                : []),

                            ...(isSubMenuAdminPermissionGranted("users")
                                ? [
                                      {
                                          name: t("user"),
                                          url: "/users",
                                          icon: <EmojiPeopleIcon />,
                                          access: ["admin", "fleetadmin"],
                                          permission: "users",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("pricing")
                                ? [
                                      {
                                          name: t("pricing"),
                                          url: "/pricing",
                                          icon: <Category />,
                                          access: ["admin"],
                                          permission: "pricing",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted(
                                "subscriptions"
                            ) && addonsdata?.data?.enable_subscription === true
                                ? [
                                      {
                                          name: t("subscription"),
                                          url: "/subscriptions",
                                          icon: <SubscriptionsIcon />,
                                          access: ["admin"],
                                          permission: "subscriptions",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("cars")
                                ? [
                                      {
                                          name: t("cars"),
                                          url: "/cars",
                                          icon: <CarIcon />,
                                          access: [
                                              "admin",
                                              "fleetadmin",
                                              "driver",
                                          ],
                                          permission: "cars",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("addtowallet")
                                ? [
                                      {
                                          name: t("add_to_wallet"),
                                          url: "/addtowallet",
                                          icon: (
                                              <AccountBalanceWalletOutlinedIcon />
                                          ),
                                          access: ["admin"],
                                          permission: "add_to_wallet",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("withdraws")
                                ? [
                                      {
                                          name: t("withdraws"),
                                          url: "/withdraws",
                                          icon: <MoneyIcon />,
                                          access: ["admin"],
                                          permission: "withdraws",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("promos")
                                ? [
                                      {
                                          name: t("promo"),
                                          url: "/promos",
                                          icon: <DiscountOutlinedIcon />,
                                          access: ["admin"],
                                          permission: "promos",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("notifications")
                                ? [
                                      {
                                          name: t("push_notification_title"),
                                          url: "/notifications",
                                          icon: (
                                              <NotificationsActiveOutlinedIcon />
                                          ),
                                          access: ["admin"],
                                          permission: "notifications",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("cancelreasons")
                                ? [
                                      {
                                          name: t("cancellation_reasons"),
                                          url: "/cancelreasons",
                                          icon: <GppBadOutlinedIcon />,
                                          access: ["admin"],
                                          permission: "cancellation_reasons",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("allreports")
                                ? [
                                      {
                                          name: t("report"),
                                          url: "/allreports",
                                          icon: <AssessmentOutlinedIcon />,
                                          access: ["admin", "fleetadmin"],
                                          permission: "reports",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("complain")
                                ? [
                                      {
                                          name: t("complain"),
                                          url: "/complain",
                                          icon: <AnnouncementOutlinedIcon />,
                                          access: ["admin"],
                                          permission: "complain",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted(
                                "admin_support_list"
                            )
                                ? [
                                      {
                                          name: t("admin_support_list"),
                                          url: "/admin-support-list",
                                          icon: <SupportAgentIcon />,
                                          access: ["admin"],
                                          permission: "admin_support_list",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted(
                                "documentmanagement"
                            ) && addonsdata?.data?.document_management
                                ? [
                                      {
                                          name: t("document_management"),
                                          url: "/documentmanagement",
                                          icon: <DocumentScannerOutlined />,
                                          access: ["admin"],
                                          permission: "documentmanagement",
                                      },
                                  ]
                                : []),
                            // ...(isSubMenuAdminPermissionGranted("roles")
                            //     ? [
                            //           {
                            //               name: t("roles_management"),
                            //               url: "/roles",
                            //               icon: <SecurityIcon />,
                            //               access: ["admin"],
                            //               permission: "roles",
                            //           },
                            //       ]
                            //     : []),
                            ...(isSubMenuAdminPermissionGranted("settings")
                                ? [
                                      {
                                          name: t("settings_title"),
                                          url: "/settings",
                                          icon: <SettingsOutlinedIcon />,
                                          access: ["admin"],
                                          permission: "settings",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("userwallet")
                                ? [
                                      {
                                          name: t("my_wallet_tile"),
                                          url: "/userwallet",
                                          icon: (
                                              <AccountBalanceWalletOutlinedIcon />
                                          ),
                                          access: [
                                              "driver",
                                              "customer",
                                              "fleetadmin",
                                          ],
                                          permission: "wallet",
                                      },
                                  ]
                                : []),
                            {
                                name: t("profile"),
                                url: "/profile",
                                icon: <PermIdentityOutlinedIcon />,
                                access: [
                                    "admin",
                                    "fleetadmin",
                                    "driver",
                                    "customer",
                                ],
                                permission: "profile",
                            },
                            {
                                name: t("logout"),
                                url: "logout",
                                icon: <LogoutOutlinedIcon />,
                                access: [
                                    "admin",
                                    "fleetadmin",
                                    "driver",
                                    "customer",
                                ],
                                permission: "logout",
                            },
                        ].map((item, index) =>
                            hasMenuAccess(item) ? (
                                <div
                                    style={{ display: "flex", height: "50px" }}
                                    key={"key" + index}
                                >
                                    <ListItem
                                        key={item}
                                        disableGutters={true}
                                        disablePadding={true}
                                        style={{ paddingLeft: 5 }}
                                        alignItems="center"
                                        dense={false}
                                    >
                                        <ListItemButton
                                            disableGutters={true}
                                            dense={false}
                                            component={Link}
                                            to={
                                                item.url === "logout"
                                                    ? null
                                                    : item.url
                                            }
                                            onClick={
                                                item.url === "logout"
                                                    ? LogOut
                                                    : null
                                            }
                                        >
                                            <ListItemIcon
                                                style={{ color: colors.WHITE }}
                                            >
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                inset={false}
                                                disableTypography={false}
                                                primary={item.name}
                                                style={{
                                                    color: colors.WHITE,
                                                    textAlign:
                                                        isRTL === "rtl"
                                                            ? "right"
                                                            : "left",
                                                }}
                                            />
                                        </ListItemButton>
                                        <Divider />
                                    </ListItem>
                                </div>
                            ) : null
                        )}
                    </List>
                ) : null}
            </div>
        </div>
    );

    const container =
        window !== undefined ? () => window().document.body : undefined;

    return (
        <Box
            sx={{ display: "flex", direction: isRTL === "rtl" ? "rtl" : "ltr" }}
        >
            <CssBaseline />
            <AppBar
                position="fixed"
                style={
                    isRTL === "rtl"
                        ? {
                              marginRight: drawerWidth,
                              backgroundColor: colors.MAIN_COLOR,
                          }
                        : {
                              marginLeft: drawerWidth,
                              backgroundColor: colors.MAIN_COLOR,
                          }
                }
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                }}
            >
                <Toolbar>
                    <IconButton
                        color={colors.WHITE}
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: "none" } }}
                    >
                        <MenuIcon style={{ color: colors.WHITE }} />
                    </IconButton>
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        style={{ color: colors.WHITE }}
                    >
                        {settings && settings.appName ? settings.appName : ""}
                    </Typography>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
                aria-label="mailbox folders"
            >
                <Drawer
                    container={container}
                    variant="temporary"
                    anchor={isRTL === "rtl" ? "right" : "left"}
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: "block", sm: "none" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                        },
                        direction: isRTL === "rtl" ? "rtl" : "ltr",
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    anchor={isRTL === "rtl" ? "right" : "left"}
                    sx={{
                        display: { xs: "none", sm: "block" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                        },
                        direction: isRTL === "rtl" ? "rtl" : "ltr",
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 1,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                }}
            >
                <Toolbar />
                {props.children}
            </Box>
        </Box>
    );
}
